import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Fire, Users, Clock } from "@phosphor-icons/react";

const PERIODS = [{ id: "today", label: "Today" }, { id: "7d", label: "7 Days" }, { id: "30d", label: "30 Days" }];
// Top-down store: [area, grid-area]. Customer areas only; staff areas shown as grey "staff only".
const AREAS = [
  { key: "Meat", label: "MEAT", area: "meat" }, { key: "Seafood", label: "FISH", area: "fish" },
  { key: "Deli", label: "DELI", area: "deli" }, { key: "Dairy", label: "DAIRY & EGGS", area: "dairy" },
  { key: "Produce", label: "FRUITS & VEGETABLES", area: "produce" }, { key: "Grocery", label: "GROCERY AISLES", area: "grocery" },
  { key: "Bakery", label: "BREAD", area: "bakery" }, { key: "Entrance", label: "ENTRANCE", area: "entr", transit: true },
  { key: "Cashier", label: "CHECKOUT", area: "reg", transit: true }, { key: "Exit", label: "EXIT", area: "exit", transit: true },
  { key: "Receiving", label: "BACK / RECEIVING — staff only", area: "back", staff: true },
  { key: "Office", label: "OFFICE — staff only", area: "office", staff: true },
];
// A V Stone (stone showroom + warehouse) has its own floor, never the grocery layout.
const AV_AREAS = [
  { key: "Showroom", label: "SHOWROOM", area: "show" }, { key: "Warehouse", label: "WAREHOUSE", area: "wh" },
  { key: "Entrance", label: "ENTRANCE", area: "entr", transit: true }, { key: "Cashier", label: "CASHIER OFFICE", area: "reg", transit: true },
];
const LAYOUTS = {
  mp: { areas: AREAS, cols: "1.3fr 1fr 1fr 1fr 0.9fr", rows: "1fr 1.6fr 0.8fr",
    tpl: `"meat fish deli dairy back" "produce grocery grocery bakery office" "entr reg reg exit office"` },
  av: { areas: AV_AREAS, cols: "1fr 1.4fr", rows: "1.4fr 0.6fr", tpl: `"show wh" "entr reg"` },
};
const HEAT = [[0.66, "#FF3B30", "VERY BUSY"], [0.33, "#FF9F0A", "BUSY"], [0.1, "#FFD60A", "SOME PEOPLE"], [0, "#3A3A3C", "QUIET"]];
const heat = (v, max) => { const s = max ? v / max : 0; return s > 0 ? HEAT.find(([t]) => s >= t) : HEAT[3]; };

function Item({ it, metric, max }) {
  const v = metric === "time" ? it.dwell_minutes : it.count;
  const [, color, word] = heat(v, max);
  return (
    <div data-testid={`sh-item-${it.camera_id}`} className="px-2 py-1.5 border border-black/30 flex items-center justify-between gap-2" style={{ background: color }}>
      <span className="font-display font-bold text-[12px] text-black truncate">{it.camera}</span>
      <span className="font-mono-data text-[10px] text-black/80 whitespace-nowrap">{metric === "time" ? `${Math.round(v)} min` : `${v} visits`}</span>
      <span className="sr-only">{word}</span>
    </div>
  );
}

function Area({ a, items, metric, max }) {
  const total = items.reduce((s, i) => s + (metric === "time" ? i.dwell_minutes : i.count), 0);
  const [, color, word] = heat(total / Math.max(1, items.length), max);
  if (a.staff) return (
    <div style={{ gridArea: a.area }} data-testid={`sh-area-${a.area}`} className="border-2 border-dashed border-white/20 bg-[repeating-linear-gradient(45deg,#1C1C1E_0_8px,#121214_8px_16px)] p-2 flex items-center justify-center">
      <span className="font-display font-bold text-[11px] text-[#71717A] text-center">{a.label}</span>
    </div>);
  return (
    <div style={{ gridArea: a.area, borderColor: color }} data-testid={`sh-area-${a.area}`} className="border-4 bg-[#121214] p-2 flex flex-col gap-1 min-h-0 overflow-auto">
      <div className="flex items-center justify-between">
        <span className="font-display font-black text-sm text-white">{a.label}</span>
        <span className="font-mono-data text-[10px] font-bold" style={{ color }}>{items.length ? word : "NO CAMERA"}</span>
      </div>
      {a.transit && <div className="text-[9px] font-mono-data text-[#71717A]">walk-through area — not a product</div>}
      <div className={`grid gap-1 ${items.length > 7 ? "grid-cols-2" : "grid-cols-1"}`}>
        {items.map((it) => <Item key={it.camera_id} it={it} metric={metric} max={max} />)}
      </div>
    </div>
  );
}

export const StoreFloor = ({ period, metric, height = "720px", refreshMs = 0 }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    const load = () => api.get(`/analytics/hot-zones?period=${period}`).then((r) => setData(r.data)).catch(() => {});
    load();
    if (!refreshMs) return undefined;
    const t = setInterval(load, refreshMs);
    return () => clearInterval(t);
  }, [period, refreshMs]);
  const zones = data?.zones || [];
  const all = [...zones, ...(data?.transit_zones || [])];
  const by = (k) => all.filter((z) => z.zone === k);
  const val = (z) => (metric === "time" ? z.dwell_minutes : z.count);
  const max = Math.max(1, ...zones.map(val));
  const L = all.some((z) => String(z.camera_id).startsWith("AV-")) ? LAYOUTS.av : LAYOUTS.mp;
  const shopKeys = L.areas.filter((a) => !a.staff && !a.transit).map((a) => a.key);
  // Only real customer sections can be "most popular" (never Punch In / staff-only cameras).
  const top = [...zones].filter((z) => shopKeys.includes(z.zone) && !/punch/i.test(z.camera) && val(z) > 0).sort((a, b) => val(b) - val(a)).slice(0, 3);
  if (!data) return <div className="text-sm font-mono-data text-[#71717A] p-6" data-testid="store-floor-loading">Loading store map…</div>;
  return (
    <div className="space-y-3" data-testid="store-floor">
      {top.length > 0 && (
        <div className="grid md:grid-cols-3 gap-2" data-testid="sh-top3">
          {top.map((z, i) => (
            <div key={z.camera_id} className="border-2 border-[#FF3B30] bg-[#FF3B30]/10 p-3" data-testid={`sh-top-${i + 1}`}>
              <div className="font-mono-data text-[10px] text-[#FF9F0A]">#{i + 1} MOST POPULAR</div>
              <div className="font-display font-black text-lg text-white">{z.camera}</div>
              <div className="font-mono-data text-[11px] text-[#A1A1AA] flex gap-3"><span className="flex items-center gap-1"><Clock size={12} />{Math.round(z.dwell_minutes)} min</span><span className="flex items-center gap-1"><Users size={12} />{z.count} visits</span></div>
            </div>
          ))}
        </div>
      )}
      <div className="grid gap-2" data-testid="sh-floor"
        style={{ height, gridTemplateColumns: L.cols, gridTemplateRows: L.rows, gridTemplateAreas: L.tpl }}>
        {L.areas.map((a) => <Area key={a.key} a={a} items={by(a.key)} metric={metric} max={max} />)}
      </div>
      <div className="flex flex-wrap gap-3 text-[11px] font-mono-data" data-testid="sh-legend">
        {HEAT.map(([, c, w]) => <span key={w} className="flex items-center gap-1"><span className="w-4 h-4 inline-block" style={{ background: c }} /> {w}</span>)}
        <span className="text-[#71717A]">Red = lots of people stand here = this item draws the most customers.</span>
      </div>
    </div>
  );
};

export default function StoreHeatMap() {
  const [period, setPeriod] = useState("today");
  const [metric, setMetric] = useState("time");
  const Toggle = ({ items, cur, set, id }) => (
    <div className="flex border border-white/10" data-testid={id}>
      {items.map((p) => <button key={p.id} data-testid={`${id}-${p.id}`} onClick={() => set(p.id)} className={`px-3 py-1.5 text-[11px] font-mono-data uppercase ${cur === p.id ? "bg-[#FF3B30] text-black" : "text-[#A1A1AA] hover:text-white"}`}>{p.label}</button>)}
    </div>
  );
  return (
    <div className="space-y-4" data-testid="store-heatmap-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="font-display font-black text-2xl flex items-center gap-2"><Fire size={24} weight="fill" className="text-[#FF3B30]" /> Where do customers stand the most?</h1>
        <div className="flex gap-2">
          <Toggle id="sh-metric" items={[{ id: "time", label: "Time spent" }, { id: "visits", label: "Visits" }]} cur={metric} set={setMetric} />
          <Toggle id="sh-period" items={PERIODS} cur={period} set={setPeriod} />
        </div>
      </div>
      <StoreFloor period={period} metric={metric} />
    </div>
  );
}
